import { ApiClient } from '@api/api-configuration';
import { BillingEntityTypeEnum, type BillingTransactionResponse } from '@api/READ_ONLY/billing_api/Api';
import { LIST_TABLE_APPEARANCE } from '@app/common/listTableAppearance';
import { DEFAULT_CURRENCY } from '@app/common/constants';
import { getNumberFormattingLocaleByLanguage } from '@app/i18n/helpers';
import type { SupportedCurrencies } from '@app/utils/pricingCurrencyUtils';
import { AppDataTable } from '@app/components/common/AppDataTable';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { CaregiverCashTxRow } from '../../types';
import { getCaregiverTransactionsTableColumns } from './caregiverTransactionsTableColumns.config';

interface CaregiverTransactionsSubTableProps {
  caregiverId: number;
}

const CaregiverTransactionsSubTable: React.FC<CaregiverTransactionsSubTableProps> = ({ caregiverId }) => {
  const { t, i18n } = useTranslation();
  const locale = getNumberFormattingLocaleByLanguage(i18n.language);

  const fetchTransactions = useCallback(
    async (params: { limit: number; offset: number; sort?: string; [key: string]: unknown }) => {
      const { limit, offset } = params;
      // The billing transactions endpoint exposes no `sort` param (verified against
      // the generated client and the deployed OpenAPI), so ordering uses the
      // backend default order.
      const response = await ApiClient.billingApi.billing.listTransactions({
        entityType: BillingEntityTypeEnum.CaregiverCashRegister,
        entityId: caregiverId,
        offset,
        limit,
      });
      return {
        data: response.data.data,
        total: response.data.total || response.data.data.length,
      };
    },
    [caregiverId]
  );

  const tableConfig = useMemo(
    () => ({
      tableId: `caregiver-cash-tx-${caregiverId}`,
      rowKey: 'id' as const,
      // Isolate from the parent table: no URL sync, no localStorage caching.
      storage: { enableUrlSync: false, enableLocalStorage: false },
      // Keep the sub-table minimal: no column-visibility manager / reset button.
      features: { columnVisibility: false },
      fetchData: fetchTransactions,
      transformData: (tx: BillingTransactionResponse): CaregiverCashTxRow => ({
        id: tx.billingTransactionId,
        transactionType: tx.transactionType,
        transactionDate: tx.transactionDate,
        amount: tx.amount,
        currency: (tx.currency as SupportedCurrencies) || DEFAULT_CURRENCY,
        notes: tx.notes,
        clientName: tx.clientName ?? null,
        visitId: tx.visitId ?? null,
      }),
      columns: getCaregiverTransactionsTableColumns(t, locale),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [caregiverId, fetchTransactions, t, i18n.language, locale]
  );

  return <AppDataTable appearance={LIST_TABLE_APPEARANCE} config={tableConfig} />;
};

export default CaregiverTransactionsSubTable;
